import { reactive, toRefs } from 'vue'
import jwtDecode from 'jwt-decode'

import router from '@/router'
import { maEnv } from '@/lib/env'
import { maCookieStorage } from '@/lib/cookie'
import { maLogger } from '@/base/logger'
import { RouteName } from '@/base/constants/route'
import { ApiError, AUTH_ERROR_CODES } from '@/api/utils/api-error'
import { useClearSession } from './clear-session'

import { MaNotification } from '@mobileaction/action-kit'

const state = reactive({
  logoutInitiated: false,
  isTokenRefresing: false,
  subscribers: [],
})

export function useTokenManager() {
  const { isTokenRefresing, subscribers } = toRefs(state)

  const onRefreshed = (token) => {
    state.subscribers.forEach(cb => cb(token))
    state.subscribers = []
  }

  const onRefreshFailed = (error) => {
    state.subscribers.forEach(cb => cb(null, error))
    state.subscribers = []
  }

  const getAccessToken = () => {
    return maCookieStorage.userAuth?.jwt || ''
  }

  const getRefreshToken = () => {
    return maCookieStorage.userAuth?.refreshToken || ''
  }

  const hasToken = () => {
    const accessToken = getAccessToken()
    const refreshToken = getRefreshToken()
    return !!(accessToken || refreshToken)
  }

  const _getDecodedToken = (token) => {
    if (!token)
      return null

    try {
      return jwtDecode(token)
    }
    catch (error) {
      maLogger.error('[useTokenManager] Failed to decode token', error)
      return null
    }
  }

  const getAccessTokenData = () => {
    const token = getAccessToken()
    return _getDecodedToken(token)
  }

  const getRefreshTokenData = () => {
    const token = getRefreshToken()
    return _getDecodedToken(token)
  }

  const setUserAuth = (authInfo) => {
    const { jwt, refreshToken } = authInfo || {}
    maCookieStorage.userAuth = { jwt, refreshToken }

    if (state.logoutInitiated) {
      state.logoutInitiated = false
    }
  }

  const updateToken = (token) => {
    const authState = maCookieStorage.userAuth
    if (!authState)
      return

    authState.jwt = token
    setUserAuth(authState)
  }

  const removeUserAuth = () => {
    delete maCookieStorage.userAuth
    delete maCookieStorage.impersonatorUsername
  }

  const _isMultipleLoginError = (errors = []) => {
    return errors.some(errorCode => errorCode == AUTH_ERROR_CODES.AUTH_MULTIPLE_LOGIN)
  }

  const _showSessionExpiredWarning = (isMultipleLogin = false) => {
    const notificationOptions = {
      title: 'Session Expired',
      description: 'Your session has expired, you have been redirected to the login page.',
      size: 'large',
    }

    if (isMultipleLogin) {
      notificationOptions.description = 'Login activity detected from another browser, you have been redirected to the login page.'
    }

    MaNotification.warning(notificationOptions)
  }

  const logoutAndCleanup = async (errors = []) => {
    if (state.logoutInitiated) {
      return
    }

    state.logoutInitiated = true

    const { clearSession } = useClearSession()
    removeUserAuth()
    clearSession()

    const isAuthError = errors?.some(errorCode => Object.values(AUTH_ERROR_CODES).includes(errorCode))
    if (isAuthError) {
      _showSessionExpiredWarning(_isMultipleLoginError(errors))
    }

    await router.push({
      name: RouteName.AUTH_LOGIN,
      query: {
        forwardTo: router.currentRoute.value.fullPath,
      },
    })
  }

  const refreshToken = async () => {
    const response = await fetch(
      `${maEnv.VITE_APP_GATEWAY_DOMAIN}/account/user/info`,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getRefreshToken()}`,
        },
      },
    )

    if (!response.ok) {
      throw new ApiError(
        '[useTokenManager] Refresh token request failed',
        {
          errorCode: AUTH_ERROR_CODES.REFRESH_TOKEN_EXPIRED,
          statusCode: response.status,
          errors: [AUTH_ERROR_CODES.REFRESH_TOKEN_EXPIRED],
        },
      )
    }

    const accessToken = response.headers.get('ma-jwt')
    updateToken(accessToken)

    return accessToken
  }

  return {
    isTokenRefresing,
    subscribers,
    onRefreshed,
    onRefreshFailed,
    getAccessToken,
    getAccessTokenData,
    getRefreshTokenData,
    getRefreshToken,
    hasToken,
    setUserAuth,
    removeUserAuth,
    refreshToken,
    updateToken,
    logoutAndCleanup,
  }
}
